<script setup lang="ts">
import type { PageHeroProps } from '#ui/types';
import lodashOmit from 'lodash-es/omit';
import lodashPick from 'lodash-es/pick';

export interface StoryblokUiPageHeroProps {
    blok: StoryblokUiPageHero;
}

// PROPS
const { blok } = defineProps<StoryblokUiPageHeroProps>();

// COMPOSABLES
const { classes } = useStoryblokComponentClass(() => lodashPick(blok, ['anchorId']));

// DATA
const pageHeroProps = computed<PageHeroProps>(() =>
    lodashOmit(storyblokOmitEmptyValues<StoryblokUiPageHero>(blok), ['_uid', 'component', 'anchorId', 'description', 'links', 'variant', 'cover'])
);
const descriptionClasses = computed(() => (blok.variant === 'gradient' ? 'prose-invert' : ''));
const ui = computed(() => {
    if (blok.variant === 'gradient') {
        return {
            root: 'from-primary-500 to-primary-700 bg-linear-to-r',
            title: 'text-inverted',
            description: '**:text-inverted',
        };
    }

    if (blok.variant === 'cover') {
        return {
            title: 'text-inverted',
            description: '**:text-inverted',
        };
    }

    return {};
});
</script>

<template>
    <UPageHero v-bind="pageHeroProps" :id="blok.anchorId" v-editable="blok" as="section" :class="classes" :ui="ui">
        <template v-if="blok.variant === 'cover' && !!blok.cover?.length" #top>
            <div class="absolute inset-0 -z-1 overflow-hidden">
                <StoryblokComponent v-for="c in blok.cover" :key="c._uid" :blok="c" />
            </div>
        </template>
        <template v-if="!!blok.description" #description>
            <StoryblokRichTextOverride :doc="blok.description" :class="descriptionClasses" class="max-w-none" />
        </template>
        <template v-if="blok.links?.length" #links>
            <StoryblokComponent v-for="l in blok.links" :key="l._uid" :blok="l" :inverted="blok.variant === 'gradient'" />
        </template>
        <template v-if="blok.body?.length" #default>
            <StoryblokComponent v-for="b in blok.body" :key="b._uid" :blok="b" class="w-full" />
        </template>
    </UPageHero>
</template>
